import React, { FC, useEffect, useRef, useState } from 'react'
import styles from './DesktopHeader.module.scss'
import SearchInput, { SearchInputPropsApi } from '../Search/SearchInput'
import IconActions from '../IconActions/IconActions'
import CategoriesCTAWrapper from './CategoriesCTA/CategoriesCTAWrapper'
import SALogo from '../IconActions/SALogo'
import classNames from 'classnames'
import { submitSearch } from '../helpers'
import SearchMenuContent from '../Search/SearchMenuContent'
import searchInputStyles from '../Search/HeaderSearch.module.scss'
import useSearchAutocomplete from '../Search/useSearchAutocomplete'
import { useSelector } from 'react-redux'
import { getSearchQuery } from 'selectors/searchProvider'
import Debounce from 'lib/debounce'
import { SearchApi } from 'types/SearchProvider'
import { ConstructorSearchService } from 'lib/constructor/services/constructor/constructorService'
import useRecentSearches from '../Search/useRecentSearches'
import { addStoreLocaleByCookie } from 'lib/storeLocale/client'
import { getUserID, isAuthenticated as getIsAuthenticated } from 'selectors/user/session'
import usePopularSearches from '../Search/usePopularSearches'
import { getIsInitialLoadReady } from 'selectors/user/isInitialLoadReady'
import HeaderSearchIconsSkeleton from './HeaderSearchIconsSkeleton'

const debounce = Debounce()

const DesktopHeader: FC = () => {
  const [isFocused, setIsFocused] = useState<boolean>(false)
  const [isExpanding, setIsExpanding] = useState<boolean>(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<SearchInputPropsApi>(null)
  const isAuthenticated = useSelector(getIsAuthenticated)
  const isInitialLoadReady = useSelector(getIsInitialLoadReady)
  const searchQuery = useSelector(getSearchQuery)
  const userId = useSelector(getUserID)
  const stringUserId = userId !== 0 ? userId.toString() : ''
  const searchService: SearchApi = new ConstructorSearchService({ userId: stringUserId })

  const {
    matchedTerm,
    suggestions,
    getAutocompleteSuggestions,
    setSuggestions,
  } = useSearchAutocomplete({
    searchService,
    maxSuggestions: 5,
  })
  const { recentSearches, addRecentSearch, removeRecentSearch } = useRecentSearches({
    userId: stringUserId,
  })
  const { popularSearches } = usePopularSearches({
    searchService,
    maxPopularSearches: 5,
  })

  const handleRemoveRecentSearchByUserId = (label: string) => {
    removeRecentSearch(label, stringUserId)
  }

  const handleOnSearchQueryChange = (query: string) => {
    debounce(() => getAutocompleteSuggestions(query || searchQuery), 500)
  }

  useEffect(() => {
    if (!isFocused) {
      return
    }
    setIsExpanding(true)
    const timeout = setTimeout(() => setIsExpanding(false), 300)
    return () => clearTimeout(timeout)
  }, [isFocused])

  const handleFocusSearchInput = (isFocusFlag: boolean) => {
    setIsFocused(isFocusFlag)
  }

  const handleFocusOnContentClick = (isFocusFlag: boolean) => {
    handleFocusSearchInput(isFocusFlag)
    searchInputRef?.current?.setIsFocused(isFocusFlag)
  }

  const handleSearchSubmit = (query: string) => {
    submitSearch(query)
    if (query.length > 0) {
      addRecentSearch(
        {
          label: query,
          link: addStoreLocaleByCookie(`/all?query=${encodeURIComponent(query)}`),
        },
        stringUserId
      )
    }
    setSuggestions([])
    handleFocusOnContentClick(false)
    searchInputRef?.current?.triggerInputBlur()
  }

  const handleCustomClearSearchCallback = () => {
    setSuggestions([])
  }

  return (
    <div className={styles.headerContainer} data-type='desktop-header'>
      <div className={styles.headerLogoActionsContainer}>
        <SALogo />

        <div className={styles.headerSearchActionsContainer}>
          {isInitialLoadReady ? (
            <>
              <div className={styles.headerSearchBox}>
                <SearchInput
                  ref={searchInputRef}
                  onFocusCallback={handleFocusSearchInput}
                  onSearch={handleSearchSubmit}
                  onChangeCallback={handleOnSearchQueryChange}
                  dropdownRef={dropdownRef}
                  onCustomClearSearchCallback={handleCustomClearSearchCallback}
                />
                <div
                  ref={dropdownRef}
                  className={classNames({
                    [searchInputStyles.searchDropdownBoxHidden]: !isFocused,
                  })}
                >
                  <SearchMenuContent
                    suggestions={suggestions}
                    recentSearches={recentSearches}
                    popularSearches={popularSearches}
                    setIsFocused={handleFocusOnContentClick}
                    onRecentSearchRemove={handleRemoveRecentSearchByUserId}
                    onSearch={handleSearchSubmit}
                    matchedTerm={matchedTerm}
                    isExpanding={isExpanding}
                  />
                </div>
              </div>
              <div
                className={classNames(styles.headerActionsBox, {
                  [styles.headerActionsBoxLogged]: isAuthenticated,
                })}
              >
                <IconActions />
              </div>
            </>
          ) : (
            <HeaderSearchIconsSkeleton />
          )}
        </div>
      </div>

      <CategoriesCTAWrapper />
    </div>
  )
}

export default DesktopHeader
